import { getAuthToken } from '../utils/auth';
import { handleUnauthorized } from '../utils/errorHandler';

export interface League {
  id: string;
  name: string;
  platform: 'yahoo' | 'sleeper' | 'espn';
  season: string;
  sport: string;
  teamCount: number;
  status: 'pre-draft' | 'drafting' | 'in-season' | 'post-season' | 'finished';
  leagueType: 'public' | 'private';
  url?: string;
  currentWeek?: number;
  totalWeeks?: number;
  startWeek?: number;
  endWeek?: number;
  draftStatus?: 'pre-draft' | 'drafting' | 'post-draft';
  isFinished?: boolean;
  leagueFormat?: string; // 'best-ball', 'keeper', 'redraft', etc.
  playoffTeams?: number;
  playoffWeekStart?: number;
  draftId?: string;
  previousLeagueId?: string;
}

export interface LeaguesResponse {
  leagues: League[];
}

const baseUrl = import.meta.env.VITE_BACKEND_BASE_URL || '';

export async function fetchLeagues(): Promise<LeaguesResponse> {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${baseUrl}/leagues`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch leagues');
  }

  const data = await response.json();
  return data;
}

interface IntegrationStatus {
  isOnboarded: boolean;
  email: string;
}

async function checkIntegrationStatus(platform: 'yahoo' | 'sleeper'): Promise<boolean> {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  try {
    const response = await fetch(`${baseUrl}/auth/${platform}/status`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return false;
    }

    const data: IntegrationStatus = await response.json();
    return data.isOnboarded;
  } catch (error) {
    console.error(`Error checking ${platform} integration:`, error);
    return false;
  }
}

export async function hasIntegrations(): Promise<boolean> {
  try {
    // Check both Yahoo and Sleeper integrations
    const [yahooStatus, sleeperStatus] = await Promise.all([
      checkIntegrationStatus('yahoo'),
      checkIntegrationStatus('sleeper')
    ]);
    
    // Return true if user has at least one integration
    return yahooStatus || sleeperStatus;
  } catch (error) {
    console.error('Error checking integrations:', error);
    throw error;
  }
}

// Matchup interfaces
export interface MatchupTeam {
  id: string;
  name: string;
  logo?: string;
  score?: number;
  projected?: number;
}

export interface Matchup {
  id: string;
  team1: MatchupTeam;
  team2: MatchupTeam;
  isComplete: boolean;
}

export interface MatchupsResponse {
  matchups: {
    week: number;
    platform: 'yahoo' | 'sleeper';
    matchups: Matchup[];
  };
}

// Fetch league details with standings
export async function fetchLeagueDetails(leagueId: string): Promise<any> {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${baseUrl}/leagues/${leagueId}/details`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch league details');
  }

  const data = await response.json();
  return data;
}

// Fetch matchups for a specific league
export async function fetchMatchups(leagueId: string, week?: number): Promise<MatchupsResponse> {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const url = week 
    ? `${baseUrl}/leagues/${leagueId}/matchups?week=${week}`
    : `${baseUrl}/leagues/${leagueId}/matchups`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch matchups');
  }

  const data = await response.json();
  return data;
}